import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTenant } from '../../context/TenantContext';
import Facts from '../../components/Facts';
import JsonView from '../../components/JsonView';
import RouteMapPanel from '../../components/RouteMapPanel';
import StatusChip from '../../components/StatusChip';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import RerunPanel from './RerunPanel';
import {
  useCancelRunMutation,
  useRunQuery,
  useRunRoutesQuery
} from '../../services/routing/queries';
import {
  DEFAULT_TENANT,
  driverLabel,
  formatDuration,
  formatWallMs,
  planEarnings,
  plannedDay,
  skipReason
} from '../../utils/format';
import type { RunCallbacks, RunRoute, RunStatus } from '../../types/routing';

const OUTCOMES = ['finished', 'pending', 'downgraded', 'error', 'skipped'];

const money = (amount: number) => `$${amount.toFixed(2)}`;

// A route whose plan recorded no amount reads as "not recorded", not as "earns nothing"; one that
// recorded only part of its rides is marked, so a low total is not mistaken for a full one.
const earns = (plan: Parameters<typeof planEarnings>[0]) => {
  const { total, counted, rides } = planEarnings(plan);
  if (!counted) return '—';
  return counted === rides ? money(total) : `${money(total)} (${counted}/${rides})`;
};

/** The outcome, and on a skipped route the reason beside it — an error code is an error only elsewhere. */
const OutcomeChips = ({ route }: { route: RunRoute }) => {
  const reason = skipReason(route);
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      <StatusChip status={route.outcome} />
      {reason && <StatusChip status="skipped" label={reason} title={route.error_detail ?? undefined} />}
    </Stack>
  );
};

const ErrorChip = ({ route }: { route: RunRoute }) =>
  route.error_code && !skipReason(route) ? (
    <Chip label={route.error_code} size="small" color="error" />
  ) : null;

const DAY_CALLBACK_LABEL: Record<RunCallbacks['routing_started'], string> = {
  off: 'off',
  sent: 'sent and received',
  failed: 'failed',
  not_made: 'not made'
};

// Before the run reaches a callback its row says "not made", which on a finished run is the
// whole story and on a live one is only "not yet".
const dayCallback = (status: RunCallbacks['routing_started'], runActive: boolean) =>
  status === 'not_made' && runActive ? 'not yet' : DAY_CALLBACK_LABEL[status];

const routeCallbacks = (tally: RunCallbacks['route_completed']) => {
  if (typeof tally === 'string') return DAY_CALLBACK_LABEL[tally];
  const lost = tally.undelivered ? `, ${tally.undelivered} undelivered` : '';
  const open = tally.in_flight ? `, ${tally.in_flight} in flight` : '';
  return `${tally.sent} sent, ${tally.received} received${lost}${open}`;
};

// A callback the receiver did not get, or a route it never heard about, is the line to look at;
// one still being answered, or not yet reached by a live run, is not settled yet.
const callbackTone = (
  value: RunCallbacks[keyof RunCallbacks],
  runActive: boolean
): RunStatus | 'done' | 'pending' => {
  if (value === 'failed' || (typeof value === 'object' && value.undelivered > 0)) return 'failed';
  if (typeof value === 'object' && value.in_flight > 0) return 'pending';
  if (value === 'not_made' && runActive) return 'pending';
  return 'done';
};

const CallbacksPanel = ({
  callbacks,
  runActive
}: {
  callbacks: RunCallbacks;
  runActive: boolean;
}) => (
  <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      Platform callbacks
    </Typography>
    <Stack spacing={0.5} component="dl" sx={{ m: 0 }}>
      {(
        [
          ['routing.started', dayCallback(callbacks.routing_started, runActive), callbacks.routing_started],
          ['route.completed', routeCallbacks(callbacks.route_completed), callbacks.route_completed],
          ['routing.finished', dayCallback(callbacks.routing_finished, runActive), callbacks.routing_finished]
        ] as const
      ).map(([event, label, value]) => (
        <Stack key={event} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography component="dt" variant="body2" sx={{ minWidth: 140, fontFamily: 'monospace' }}>
            {event}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0 }}>
            <StatusChip status={callbackTone(value, runActive)} label={label} />
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Paper>
);

const Tile = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Paper sx={{ p: 1.5 }}>
    <Typography variant="h6">{value}</Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

const span = (from: string | null | undefined, to: string | null | undefined) =>
  from ? `${from} → ${to ?? ''}` : '';

/** A link out of a card or row that must not also select the route behind it. */
const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * One route per card, for a screen too narrow to read one per row. Tapping the card shows the
 * route on the map, as a row click does; the id and "detail" still open its full page.
 */
const RouteCards = ({
  runId,
  routes,
  selectedRouteId,
  onSelect
}: {
  runId: string;
  routes: RunRoute[];
  selectedRouteId: number | null;
  onSelect: (routeId: number) => void;
}) => (
  <Stack spacing={1}>
    {routes.length === 0 && <Typography color="text.secondary">No routes match.</Typography>}
    {routes.map(route => (
      <Paper
        key={route.route_id}
        variant="outlined"
        role="button"
        tabIndex={0}
        aria-label={`show route ${route.route_id} on the map`}
        aria-pressed={route.route_id === selectedRouteId}
        onClick={() => onSelect(route.route_id)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(route.route_id);
          }
        }}
        sx={{
          p: 1.5,
          cursor: 'pointer',
          bgcolor: route.route_id === selectedRouteId ? 'action.selected' : undefined
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Link
            component={RouterLink}
            to={`/runs/${runId}/routes/${route.route_id}`}
            underline="hover"
            variant="subtitle1"
            sx={{ fontWeight: 650 }}
            onClick={stop}
          >
            Route {route.route_id}
          </Link>
          <StatusChip status={route.status} />
          <OutcomeChips route={route} />
          <ErrorChip route={route} />
          <Link
            component={RouterLink}
            to={`/runs/${runId}/routes/${route.route_id}`}
            underline="hover"
            variant="body2"
            whiteSpace="nowrap"
            sx={{ ml: 'auto' }}
            onClick={stop}
          >
            detail →
          </Link>
        </Stack>
        <Facts
          items={[
            ['fleet', route.fleet_name],
            ['driver', driverLabel(route)],
            ['rides', route.rides_assigned],
            ['shift (UTC)', span(route.route_from_datetime, route.route_to_datetime)],
            ['earns', earns(route.plan)],
            ['planned', span(route.planned_start_at, route.planned_end_at)],
            ['pools', route.pools_assigned],
            ['engine calls', route.engine_calls],
            ['fallbacks', route.engine_fallbacks],
            ['wall', formatWallMs(route.wall_ms)]
          ]}
        />
      </Paper>
    ))}
  </Stack>
);

export const RunDetailPage = () => {
  const { runId = '' } = useParams();
  const { tenant, setTenant } = useTenant();
  const { enqueueSnackbar } = useSnackbar();
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [rerunOpen, setRerunOpen] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const compact = useCompactLayout();

  useEffect(() => {
    // The list is long, so bring the map to the reader rather than making them scroll back up.
    // Optional call: jsdom has no scrollIntoView.
    if (selectedRouteId != null) mapRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedRouteId]);

  const { data: run, isLoading, error } = useRunQuery(runId);
  const runActive = run?.status === 'queued' || run?.status === 'running';
  const { data: routes } = useRunRoutesQuery(runId, runActive);
  const cancelMutation = useCancelRunMutation(runId);

  if (isLoading) return <CircularProgress aria-label="loading run" />;
  if (error) return <Alert severity="error">{(error as Error).message}</Alert>;
  if (!run) return null;

  const progress = run.progress;
  const percent = progress.routes_total
    ? Math.round((100 * progress.routes_done) / progress.routes_total)
    : 0;
  const filteredRoutes = (routes ?? []).filter(
    route => !outcomeFilter || route.outcome === outcomeFilter
  );
  const selectedRoute = (routes ?? []).find(route => route.route_id === selectedRouteId);

  const onCancel = () => {
    if (!window.confirm(`Cancel run #${runId}? Routes already running will finish.`)) return;
    cancelMutation.mutate(undefined, {
      onSuccess: result =>
        enqueueSnackbar(result.canceled ? 'Run canceled' : 'Run had already finished'),
      onError: mutationError =>
        enqueueSnackbar((mutationError as Error).message, { variant: 'error' })
    });
  };

  return (
    <Stack spacing={2}>
      {run.tenant && run.tenant !== tenant && (
        <Alert
          severity="info"
          action={
            <Button size="small" onClick={() => setTenant(run.tenant as string)}>
              Switch to {run.tenant}
            </Button>
          }
        >
          This run planned for tenant “{run.tenant}”, but “{tenant}” is selected — the engine chip
          and every tenant-scoped page above describe “{tenant}”, not this run.
        </Alert>
      )}
      <Link component={RouterLink} to="/runs" underline="hover">
        ← All runs
      </Link>

      <Paper sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 2 }}
          alignItems="center"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="h6">Run #{run.run_id}</Typography>
          <StatusChip status={run.status} />
          {runActive && (
            <Button color="error" variant="outlined" size="small" onClick={onCancel}>
              Cancel run
            </Button>
          )}
          {/* Only once the run is over: a rerun copies the routes the run queued, and half a
              queue is not a plan worth comparing against. */}
          {!runActive && (
            <Button variant="outlined" size="small" onClick={() => setRerunOpen(open => !open)}>
              {rerunOpen ? 'Close re-run' : 'Re-run with new config'}
            </Button>
          )}
          {run.rerun_of ? (
            <Button
              variant="outlined"
              size="small"
              component={RouterLink}
              to={`/runs/${run.run_id}/compare`}
            >
              Compare with #{run.rerun_of}
            </Button>
          ) : null}
        </Stack>
        {run.rerun_of ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            This run re-plans{' '}
            <Link component={RouterLink} to={`/runs/${run.rerun_of}`} underline="hover">
              run #{run.rerun_of}
            </Link>{' '}
            under a configuration of its own
            {run.platform_callbacks === 'off'
              ? ', and told the platform nothing — the day it planned is untouched.'
              : ', and told the platform, so the day it planned was replanned for real.'}{' '}
            {run.world_pinned_from
              ? `It planned the very rides and routes run #${run.world_pinned_from} saw, so the plans differ where the configuration does — and, past one worker, where routes raced for rides.`
              : `It planned the rides in the world at the time, not the ones run #${run.rerun_of} saw — that run recorded no world to pin to, or the live one was asked for.`}
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3} md={1.7}>
            <Tile label="trigger" value={run.trigger} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.7}>
            <Tile label="engine" value={run.engine ?? '—'} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.7}>
            <Tile label="planned day (NY)" value={plannedDay(run.from_datetime)} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.7}>
            <Tile
              label="routes done"
              value={`${progress.routes_done}/${progress.routes_total}`}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.7}>
            <Tile label="rides assigned" value={progress.rides_assigned} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.8}>
            <Tile label="duration" value={formatDuration(run.started_at, run.finished_at) || '—'} />
          </Grid>
        </Grid>
        <LinearProgress
          variant="determinate"
          value={percent}
          aria-label="run progress"
          sx={{ mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, overflowWrap: 'anywhere' }}>
          window {run.from_datetime} → {run.to_datetime} UTC
          {run.fleet_ids?.length ? ` · fleets ${run.fleet_ids.join(', ')}` : ''}
          {run.company_ids?.length ? ` · companies ${run.company_ids.join(', ')}` : ''}
          {run.requested_by_admin_id !== null ? ` · admin ${run.requested_by_admin_id}` : ''}
        </Typography>
        {run.callbacks ? <CallbacksPanel callbacks={run.callbacks} runActive={runActive} /> : null}
        {run.overrides?.length ? (
          <JsonView title="Overrides (the rules this run alone planned with)" value={run.overrides} />
        ) : null}
        <JsonView title="Config snapshot (what this run planned with)" value={run.config_snapshot} />
        <JsonView title="Stats" value={run.stats} />
      </Paper>

      {rerunOpen && (
        <RerunPanel
          runId={runId}
          // The tenant the server will re-plan, which for a run that recorded none is the
          // service default — never the header's, or the panel would show one tenant's values
          // for a rerun that plans another's.
          tenant={run.tenant ?? DEFAULT_TENANT}
          onClose={() => setRerunOpen(false)}
        />
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" color="text.secondary">
            ROUTES
          </Typography>
          <TextField
            select
            label="Outcome"
            size="small"
            value={outcomeFilter}
            onChange={event => setOutcomeFilter(event.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">any</MenuItem>
            {OUTCOMES.map(value => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            {filteredRoutes.length} of {(routes ?? []).length} routes
          </Typography>
          <Typography variant="caption" color="text.secondary">
            in the order the run works them — each fleet group before the next
          </Typography>
        </Stack>
        {selectedRoute && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }} ref={mapRef}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="subtitle2" color="text.secondary">
                ROUTE {selectedRoute.route_id} ON MAP
              </Typography>
              <StatusChip status={selectedRoute.outcome} />
              <Typography variant="caption" color="text.secondary">
                {selectedRoute.rides_assigned} rides · {selectedRoute.pools_assigned} pools
              </Typography>
              <Link
                component={RouterLink}
                to={`/runs/${runId}/routes/${selectedRoute.route_id}`}
                underline="hover"
                variant="caption"
              >
                open full detail
              </Link>
              <Link
                component="button"
                variant="caption"
                underline="hover"
                onClick={() => setSelectedRouteId(null)}
              >
                hide map
              </Link>
            </Stack>
            <RouteMapPanel
              runId={runId}
              routeId={String(selectedRoute.route_id)}
              plan={selectedRoute.plan ?? []}
            />
          </Paper>
        )}
        {compact ? (
          <RouteCards
            runId={runId}
            routes={filteredRoutes}
            selectedRouteId={selectedRouteId}
            onSelect={setSelectedRouteId}
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Route</TableCell>
                  <TableCell>Fleet</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Shift window (UTC)</TableCell>
                  <TableCell>Planned span</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell align="right">Rides</TableCell>
                  <TableCell align="right">Earns</TableCell>
                  <TableCell align="right">Pools</TableCell>
                  <TableCell align="right">Engine calls</TableCell>
                  <TableCell align="right">Fallbacks</TableCell>
                  <TableCell align="right">Wall</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoutes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={15}>
                      <Typography color="text.secondary">No routes match.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filteredRoutes.map(route => (
                  <TableRow
                    key={route.route_id}
                    hover
                    selected={route.route_id === selectedRouteId}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedRouteId(route.route_id)}
                    aria-label={`show route ${route.route_id} on the map`}
                  >
                    <TableCell>
                      {/* The row shows the route on the map; the id still opens its full detail. */}
                      <Link
                        component={RouterLink}
                        to={`/runs/${runId}/routes/${route.route_id}`}
                        underline="hover"
                        onClick={stop}
                      >
                        {route.route_id}
                      </Link>
                    </TableCell>
                    <TableCell>{route.fleet_name}</TableCell>
                    <TableCell>{driverLabel(route)}</TableCell>
                    <TableCell>
                      {route.route_from_datetime ?? ''} → {route.route_to_datetime ?? ''}
                    </TableCell>
                    <TableCell>{span(route.planned_start_at, route.planned_end_at)}</TableCell>
                    <TableCell>
                      <StatusChip status={route.status} />
                    </TableCell>
                    <TableCell>
                      <OutcomeChips route={route} />
                    </TableCell>
                    <TableCell align="right">{route.rides_assigned}</TableCell>
                    <TableCell align="right">{earns(route.plan)}</TableCell>
                    <TableCell align="right">{route.pools_assigned}</TableCell>
                    <TableCell align="right">{route.engine_calls}</TableCell>
                    <TableCell align="right">{route.engine_fallbacks}</TableCell>
                    <TableCell align="right">{formatWallMs(route.wall_ms)}</TableCell>
                    <TableCell>
                      <ErrorChip route={route} />
                    </TableCell>
                    <TableCell>
                      {/* The row shows the route on the map, so opening its detail needs a control
                          of its own rather than only the id at the far left of a wide table. */}
                      <Link
                        component={RouterLink}
                        to={`/runs/${runId}/routes/${route.route_id}`}
                        underline="hover"
                        variant="body2"
                        whiteSpace="nowrap"
                        onClick={stop}
                      >
                        detail →
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
};

export default RunDetailPage;
